/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { HTMLAttributes, useEffect, useState } from 'preact/compat';
import { Container } from '@adobe-commerce/elsie/lib';
import { QuoteCommentsList as QuoteCommentsListComponent } from '@/quote-management/components/QuoteCommentsList';
import { NegotiableQuoteTemplateModel } from '@/quote-management/data/models/negotiable-quote-template-model';
import { events } from '@adobe-commerce/event-bus';
import { formattedDate } from '@/quote-management/utils/dateUtils';

export interface QuoteTemplateCommentsListProps extends HTMLAttributes<HTMLUListElement> {
  templateData?: NegotiableQuoteTemplateModel;
}

export const QuoteTemplateCommentsList: Container<QuoteTemplateCommentsListProps> = ({
  templateData: initialData,
  ...props
}) => {
  const [templateData, setTemplateData] = useState<NegotiableQuoteTemplateModel | undefined>(initialData);

  useEffect(() => {
    const templateDataEvent = events.on('quote-management/quote-template-data', (data: { quoteTemplate: NegotiableQuoteTemplateModel }) => {
      setTemplateData(data.quoteTemplate);
    }, { eager: true });
    return () => templateDataEvent?.off();
  }, []);

  if (!templateData) {
    return null;
  }

  // Format comments data
  const formattedComments = templateData?.comments?.map((comment) => ({
    uid: comment.uid,
    createdAt: <span>{formattedDate(comment.createdAt, 'long')}</span>,
    author: (
      <span>
        {comment.author.firstname} {comment.author.lastname}
      </span>
    ),
    text: <p>{comment.text}</p>,
    attachments: comment.attachments,
  }));

  return (
    <QuoteCommentsListComponent
      data-testid="quote-template-comments-list-container"
      {...props}
      comments={formattedComments || []}
    />
  );
};
